import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { usePagination } from "@/hooks/usePagination";
import { Button } from "@/components/ui/button";
import { throwIfNotOk } from "@/lib/api-error";
import { uploadFile } from "@/lib/upload";
import { toast } from "@/lib/toast";
import { Pagination } from "@/components/ui/pagination";
import { Upload } from "@phosphor-icons/react";
import { MediaCard } from "@/components/admin/media/MediaCard";
import { MediaDetailDialog } from "@/components/admin/media/MediaDetailDialog";
import { MediaUploadDialog } from "@/components/admin/media/MediaUploadDialog";
import { MediaFolderTabs } from "@/components/admin/media/MediaFolderTabs";
import { MediaEmptyState } from "@/components/admin/media/MediaEmptyState";
import { MediaSkeletonCard } from "@/components/admin/media/MediaSkeletonCard";
import type { MediaItem } from "@/types";

const fetchMedia = async (params: { folder?: string; search?: string; page?: number }) => {
  const sp = new URLSearchParams();
  if (params.folder) sp.set("folder", params.folder);
  if (params.search) sp.set("search", params.search);
  sp.set("page", String(params.page || 1));
  sp.set("limit", "24");
  const res = await fetch(`/api/media?${sp}`);
  await throwIfNotOk(res, "Greška pri učitavanju medija");
  return res.json() as Promise<{ data: MediaItem[]; total: number; page: number }>;
};

export function MediaSection() {
  const queryClient = useQueryClient();
  const { page, setPage, searchQuery, setSearch, filters, setFilter } =
    usePagination<{ folder: string }>({ pageSize: 24 }, { folder: "" });
  const { folder } = filters;
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "media", folder, searchQuery, page],
    queryFn: () =>
      fetchMedia({
        folder: folder || undefined,
        search: searchQuery || undefined,
        page,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/media/${id}`, { method: "DELETE" });
      await throwIfNotOk(res, "Greška pri brisanju medija");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "media"] });
      toast.success("Medij uspješno obrisan.");
      setSelectedMedia(null);
    },
  });

  const handleUpload = async (file: File, uploadFolder: string) => {
    setIsUploading(true);
    try {
      await uploadFile(file, uploadFolder);
      queryClient.invalidateQueries({ queryKey: ["admin", "media"] });
      toast.success("Fajl uspješno uploadovan.");
      setUploadOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Greška pri uploadu.");
    } finally {
      setIsUploading(false);
    }
  };

  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 24);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-end">
        <Button onClick={() => setUploadOpen(true)}>
          <Upload className="size-4" />
          Upload
        </Button>
      </div>

      <MediaFolderTabs
        folder={folder}
        searchQuery={searchQuery}
        onFolderChange={(v) => setFilter("folder", v)}
        onSearchChange={setSearch}
      />

      {isLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {[...Array(12)].map((_, i) => (
            <MediaSkeletonCard key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <MediaEmptyState hasSearch={searchQuery.length > 0} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {items.map((item) => (
              <MediaCard key={item.id} item={item} onClick={() => setSelectedMedia(item)} />
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      {selectedMedia && (
        <MediaDetailDialog
          item={selectedMedia}
          isDeleting={deleteMutation.isPending}
          onDelete={() => deleteMutation.mutate(selectedMedia.id)}
          onClose={() => setSelectedMedia(null)}
        />
      )}

      {uploadOpen && (
        <MediaUploadDialog isUploading={isUploading} onUpload={handleUpload} onClose={() => setUploadOpen(false)} />
      )}
    </div>
  );
}
